import React, { FC, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button, Input, message, Spin, Typography } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  PrinterOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { backendUrl } from "config";

// Set worker path for PDF.js - using local file to avoid CORS issues (ตามตัวอย่าง PdfReader)
if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf-worker/pdf.worker.min.js";
}

// URL สำหรับทดสอบ viewer (เบื้องต้นใช้ sample PDF ก่อน)
// const SAMPLE_PDF_URL = "https://pdfobject.com/pdf/sample.pdf";

// แปลง image_url ให้เป็น full URL: รองรับ blob:, http(s), หรือ relative path
function normalizePdfUrl(url: string | null): string {
  if (!url) return "";
  if (url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = (backendUrl ?? "").replace(/\/$/, "");
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
}

type ViewMode = "single" | "all";

interface PdfViewerModalProps {
  pdfUrl: string | null;
  title?: string;
  onLoadError?: (error: Error) => void;
  /** เรียกหลังสั่งพิมพ์สำเร็จ (เปิดหน้าพิมพ์หรือดาวน์โหลดแล้ว) — ใช้สำหรับมาร์ก is_print ใน merge preview */
  onAfterPrint?: () => void;
  /** เมื่อ true แสดงปุ่มสลับ "ดูทีละหน้า" / "แสดงทั้งหมด" (สำหรับ Label รวม หลายหน้า) */
  showAllPagesOption?: boolean;
  /** โหมดการแสดงผลเริ่มต้น (default คือ "single") */
  defaultViewMode?: ViewMode;
}

const PdfViewerModal: FC<PdfViewerModalProps> = ({
  pdfUrl,
  onLoadError,
  onAfterPrint,
  showAllPagesOption,
  defaultViewMode = "single",
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [scale, setScale] = useState(1.0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [printLoading, setPrintLoading] = useState(false);
  /** โหมดแสดง: single = ดูทีละหน้า (default), all = แสดงทุกหน้ายาวๆ scroll ได้ */
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);

  // เบื้องต้นใช้ sample PDF เพื่อทดสอบ; ถ้ามี pdfUrl ให้ใช้ URL จริง
  const displayUrl = normalizePdfUrl(pdfUrl);

  /** แสดงปุ่มสลับโหมดเมื่อ caller ขอ หรือ PDF มีมากกว่า 1 หน้า */
  const enableViewModeToggle =
    Boolean(showAllPagesOption) || (numPages != null && numPages > 1);

  // รีเซ็ตเป็น single (หรือค่าเริ่มต้น) ทุกครั้งที่เปิด PDF ใหม่ (เช่น เปิด Modal Label รวม)
  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [displayUrl, defaultViewMode]);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setPageNumber(1);
    setPageInput("1");
    setLoadError(null);
    // ไม่ set viewMode ที่นี่ — ถ้า defaultViewMode="all" แล้วผู้ใช้กด "ดูทีละหน้า"
    // Document โหลดใหม่จะยิง onLoadSuccess อีกครั้งและทับกลับเป็น "all"
  };

  const onDocumentLoadError = (error: Error) => {
    setLoadError(error?.message || "โหลด PDF ไม่ได้");
    onLoadError?.(error);
  };

  const goToPrevPage = () => {
    const newPage = Math.max(pageNumber - 1, 1);
    setPageNumber(newPage);
    setPageInput(String(newPage));
  };

  const goToNextPage = () => {
    const newPage = Math.min(pageNumber + 1, numPages || 1);
    setPageNumber(newPage);
    setPageInput(String(newPage));
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.6));

  const downloadPdf = () => {
    if (displayUrl) {
      const link = document.createElement("a");
      link.href = displayUrl;
      link.download = `document_${Date.now()}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /** ปริ้น PDF: โหลดเป็น blob → เปิดหน้าใหม่ → สั่ง print
   * เรียก onAfterPrint ทันทีหลัง trigger print — ไม่รอ printWindow.onload
   * เพราะ blob: URL โหลดแบบ synchronous ทำให้ onload อาจ fire ก่อนผูก handler ทัน */
  const printPdf = async () => {
    if (!displayUrl) return;
    setPrintLoading(true);
    try {
      const res = await fetch(displayUrl);
      if (!res.ok) throw new Error("โหลด PDF ไม่สำเร็จ");
      const bytes = await res.arrayBuffer();
      const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        // เรียก onAfterPrint ทันที — ไม่รอ onload ซึ่งอาจไม่ fire สำหรับ blob: URL
        onAfterPrint?.();
        // พยายามสั่ง print หลัง window โหลด (best-effort ไม่กระทบการมาร์กสถานะ)
        printWindow.onload = () => {
          try {
            printWindow.print();
          } finally {
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
          }
        };
        // Fallback: ถ้า onload ไม่ fire (blob URL โหลดเร็วเกิน) — รอ 500ms แล้วสั่ง print
        setTimeout(() => {
          try { printWindow.print(); } catch { /* ignore */ }
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        }, 500);
      } else {
        message.info("เปิดหน้าต่างพิมพ์ไม่ได้ กรุณาอนุญาต pop-up หรือดาวน์โหลดไฟล์แทน");
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `label_${Date.now()}.pdf`;
        a.click();
        onAfterPrint?.();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch {
      message.warning("โหลด PDF ไม่ได้ (อาจติด CORS) — เปิดลิงก์ในแท็บใหม่แทน");
      const fallbackWindow = window.open(displayUrl, "_blank");
      if (fallbackWindow) {
        onAfterPrint?.(); // เรียกทันทีเช่นกัน
        fallbackWindow.onload = () => {
          try { fallbackWindow.print(); } catch { /* ignore */ }
        };
      }
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) setPageInput(value);
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNum = parseInt(pageInput, 10);
    if (pageNum >= 1 && pageNum <= (numPages || 1)) {
      setPageNumber(pageNum);
    } else {
      setPageInput(String(pageNumber));
    }
  };

  // อัปเดต pageInput เมื่อ pageNumber เปลี่ยน
  useEffect(() => {
    setPageInput(String(pageNumber));
  }, [pageNumber]);

  // คำนวณความกว้างหน้า PDF ให้พอดีกับความสูง viewport (ไม่ต้อง scroll ลง)
  const [pageWidth, setPageWidth] = useState(() =>
    typeof window !== "undefined"
      ? Math.min(480, Math.max(260, (window.innerHeight * 0.7 - 100) / 1.414))
      : 400
  );
  useEffect(() => {
    const updateWidth = () => {
      if (typeof window === "undefined") return;
      setPageWidth(Math.min(480, Math.max(260, (window.innerHeight * 0.7 - 100) / 1.414)));
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // คีย์ลัด
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (event.key === "ArrowRight") goToNextPage();
      if (event.key === "ArrowLeft") goToPrevPage();
      if (event.key === "=" || (event.key === "+" && event.ctrlKey)) {
        event.preventDefault();
        zoomIn();
      }
      if (event.key === "-" && event.ctrlKey) {
        event.preventDefault();
        zoomOut();
      }
      if (event.key === "d" && event.ctrlKey) {
        event.preventDefault();
        downloadPdf();
      }
      if (event.key === "p" && event.ctrlKey) {
        event.preventDefault();
        printPdf();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pageNumber, numPages, scale, displayUrl]);

  if (!displayUrl) {
    return (
      <PdfWrapper>
        <Typography.Text type="secondary">ไม่มี URL ของ PDF</Typography.Text>
      </PdfWrapper>
    );
  }

  if (loadError) {
    return (
      <PdfWrapper>
        <Typography.Text type="danger">{loadError}</Typography.Text>
        <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
          {/* <a href={displayUrl} target="_blank" rel="noopener noreferrer">
            เปิดในแท็บใหม่
          </a> */}
        </Typography.Text>
      </PdfWrapper>
    );
  }

  return (
    <PdfWrapper>
      <PdfToolbar>
        <ToolbarLeft>
          {/* <Button type="link" size="small" icon={<LinkOutlined />} href={displayUrl} target="_blank" rel="noopener noreferrer">
            เปิดในแท็บใหม่
          </Button> */}
          {enableViewModeToggle && (
            <span style={{ marginLeft: 8 }}>
              <Button
                type={viewMode === "all" ? "primary" : "default"}
                size="small"
                onClick={() => setViewMode("all")}
              >
                แสดงทั้งหมด
              </Button>
              <Button
                type={viewMode === "single" ? "primary" : "default"}
                size="small"
                onClick={() => {
                  setViewMode("single");
                  setPageNumber(1);
                  setPageInput("1");
                }}
                style={{ marginLeft: 4 }}
              >
                ดูทีละหน้า
              </Button>
            </span>
          )}
        </ToolbarLeft>
        <ToolbarCenter>
          <Button type="text" size="small" icon={<ZoomOutOutlined />} onClick={zoomOut} title="ซูมออก (Ctrl+-)" />
          <span className="scale-text">{Math.round(scale * 100)}%</span>
          <Button type="text" size="small" icon={<ZoomInOutlined />} onClick={zoomIn} title="ซูมเข้า (Ctrl++)" />
          <Button type="text" size="small" icon={<DownloadOutlined />} onClick={downloadPdf} title="ดาวน์โหลด (Ctrl+D)" />
          <Button type="text" size="small" icon={<PrinterOutlined />} onClick={printPdf} loading={printLoading} title="พิมพ์ (Ctrl+P)" />
        </ToolbarCenter>
      </PdfToolbar>

      <PdfScrollArea $viewMode={viewMode}>
        <Document
          file={displayUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <LoadingOverlay>
              <Spin size="large" tip="กำลังโหลด PDF..." />
            </LoadingOverlay>
          }
        >
          {viewMode === "single" ? (
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ) : (
            numPages != null &&
            Array.from({ length: numPages }, (_, i) => (
              <div key={i + 1} className="page-wrap">
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            ))
          )}
        </Document>
      </PdfScrollArea>

      {viewMode === "single" && (
        <PdfNav>
          <Button
            type="primary"
            size="small"
            icon={<LeftOutlined />}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            ก่อนหน้า
          </Button>
          <form onSubmit={handlePageInputSubmit} className="page-form">
            <span className="page-label">หน้า</span>
            <Input
              type="text"
              value={pageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageInputSubmit}
              className="page-input"
            />
            <span className="page-label">จาก {numPages ?? "–"}</span>
          </form>
          <Button
            type="primary"
            size="small"
            icon={<RightOutlined />}
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1)}
          >
            ถัดไป
          </Button>
        </PdfNav>
      )}
    </PdfWrapper>
  );
};

export default PdfViewerModal;

const PdfWrapper = styled.div`
  padding: 0;
  height: 70vh;
  min-height: 360px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const LoadingOverlay = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const PdfToolbar = styled.div`
  flex-shrink: 0;
  padding: 8px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const ToolbarLeft = styled.div``;
const ToolbarCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  .scale-text {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
    min-width: 40px;
    text-align: center;
  }
`;

/** พื้นที่ scroll — สลับสไตล์ตามโหมด single / all โดยไม่ unmount Document */
const PdfScrollArea = styled.div<{ $viewMode: ViewMode }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0;
  overflow: ${(p) => (p.$viewMode === "single" ? "hidden" : "auto")};
  justify-content: ${(p) => (p.$viewMode === "single" ? "center" : "flex-start")};
  gap: ${(p) => (p.$viewMode === "all" ? "16px" : "0")};

  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${(p) => (p.$viewMode === "all" ? "16px" : "0")};
  }
  .page-wrap {
    display: flex;
    justify-content: center;
  }
  .react-pdf__Page {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }
`;

const PdfNav = styled.div`
  flex-shrink: 0;
  padding: 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  .page-form {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .page-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
  }
  .page-input {
    width: 48px;
    text-align: center;
    font-size: 12px;
  }
`;
